"use client"

import { memo, useCallback, useMemo, useRef, useState } from "react"
import Image from "next/image"

import { RUNS_FILTER_STORAGE_KEY } from "@/constants/storage-keys"
import { useOrganization, useOrganizationList } from "@clerk/nextjs"
import type { OrganizationMembershipResource } from "@clerk/shared/types"
import {
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  SearchInput,
  Skeleton,
} from "@coval/ui"
import { Building2, Check } from "lucide-react"

import { logger } from "@/lib/logger"
import {
  ESCAPE_SHORTCUT_PRIORITY,
  useEscapeShortcut,
} from "@/hooks/useEscapeShortcut"
import { buildOrganizationHomePath } from "@/utils/workspaceRoutes"

const log = logger("DevOrganizationSwitcher")
const DIALOG_TITLE = "Switch organization"

interface DevOrganizationSwitcherModalProps {
  isOpen: boolean
  onClose: () => void
}

interface OrganizationButtonProps {
  membership: OrganizationMembershipResource
  isCurrentOrg: boolean
  isLoading: boolean
  isHighlighted: boolean
  onPress: (membership: OrganizationMembershipResource) => void
  buttonRef?: React.RefObject<HTMLButtonElement>
}

const OrganizationButton = memo(
  ({
    membership,
    isCurrentOrg,
    isLoading,
    isHighlighted,
    onPress,
    buttonRef,
  }: OrganizationButtonProps) => (
    <Button
      ref={buttonRef}
      variant="secondary"
      className={cn(
        "h-auto min-h-20 w-full justify-start gap-3 whitespace-normal rounded-xl border px-4 py-3 text-left shadow-none",
        "transition-colors duration-150",
        isCurrentOrg
          ? "border-coval-border-active bg-surface-1"
          : isHighlighted
            ? "border-coval-border-active bg-surface-1 ring-1 ring-coval-border-active"
            : "border-coval-border bg-surface-default hover:border-coval-border-active hover:bg-surface-default-hover",
      )}
      onClick={() => onPress(membership)}
      disabled={isLoading}
      aria-label={
        isCurrentOrg
          ? `${membership.organization.name}, current organization`
          : `Switch to ${membership.organization.name}`
      }
    >
      {membership.organization.imageUrl ? (
        <div className="relative size-11 shrink-0 overflow-hidden rounded-lg border border-coval-border bg-surface-default">
          <Image
            src={membership.organization.imageUrl}
            alt={membership.organization.name}
            fill
            className="object-cover"
          />
        </div>
      ) : (
        <div className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-coval-border bg-surface-2 text-sm font-semibold text-coval-text">
          {membership.organization.name.charAt(0).toUpperCase()}
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col items-start justify-center gap-0.5">
        <span className="w-full truncate text-sm font-medium text-coval-text">
          {membership.organization.name}
        </span>
        <span className="w-full truncate text-xs font-normal text-coval-text-muted">
          {membership.organization.slug ?? "No organization slug"}
        </span>
      </div>
      {isCurrentOrg && (
        <Check
          aria-hidden="true"
          className="size-4 shrink-0 text-coval-text-muted"
        />
      )}
    </Button>
  ),
)

OrganizationButton.displayName = "OrganizationButton"

export const DevOrganizationSwitcherModal = ({
  isOpen,
  onClose,
}: DevOrganizationSwitcherModalProps) => {
  // SIMPLE APPROACH: No infinite scrolling, just basic pagination
  const { setActive, userMemberships, isLoaded } = useOrganizationList({
    userMemberships: {
      pageSize: 100, // Get up to 100 organizations at once
      keepPreviousData: true, // Keep data while loading next page
    },
  })

  const { organization: currentOrg } = useOrganization()

  const [searchTerm, setSearchTerm] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const selectedButtonRef = useRef<HTMLButtonElement>(null)

  // Simple filtered organizations with search
  const filteredOrganizations = useMemo(() => {
    if (!userMemberships?.data) {
      return []
    }

    const filtered = userMemberships.data.filter((membership) =>
      membership.organization.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
    )

    // Sort alphabetically
    return filtered.toSorted((a, b) =>
      a.organization.name.localeCompare(b.organization.name),
    )
  }, [userMemberships?.data, searchTerm])

  // Handle search with auto-select first result (no useEffect needed)
  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value)
    setSelectedIndex(0) // Auto-select first result when search changes
  }, [])

  // Organization switching with proper URL navigation (like main Clerk switcher)
  const handleOrganizationSwitch = useCallback(
    async (membership: OrganizationMembershipResource) => {
      if (currentOrg?.id === membership.organization.id) {
        onClose()
        return
      }

      const newOrgSlug = membership.organization.slug

      setIsLoading(true)
      try {
        if (setActive) {
          // Clear localStorage filters when switching organizations
          if (typeof window !== "undefined") {
            try {
              window.localStorage.removeItem(RUNS_FILTER_STORAGE_KEY)
              const keysToRemove: string[] = []
              for (let i = 0; i < window.localStorage.length; i++) {
                const key = window.localStorage.key(i)
                if (key?.startsWith("run-filter-")) {
                  keysToRemove.push(key)
                }
              }
              keysToRemove.forEach((key) => window.localStorage.removeItem(key))
            } catch (error: unknown) {
              log.warn("Error clearing filters on org switch", {
                error: error instanceof Error ? error.message : String(error),
              })
            }
          }

          if (newOrgSlug) {
            await setActive({
              organization: membership.organization.id,
              redirectUrl: buildOrganizationHomePath(newOrgSlug),
            })
            onClose()
          } else {
            await setActive({ organization: membership.organization.id })
            window.location.reload()
          }
          setIsLoading(false)
        }
      } catch (error: unknown) {
        log.error(
          "Failed to switch organization",
          error instanceof Error ? error : undefined,
        )
        setIsLoading(false)
      }
    },
    [setActive, currentOrg?.id, onClose],
  )

  // Simple close handler
  const handleClose = useCallback(() => {
    if (isLoading) {
      return
    }
    setSearchTerm("")
    onClose()
  }, [isLoading, onClose])

  useEscapeShortcut({
    allowInInputs: true,
    description: "Close organization switcher",
    enabled: isOpen,
    handler: handleClose,
    priority: ESCAPE_SHORTCUT_PRIORITY.DIALOG,
  })

  // Enhanced keyboard handler with arrow navigation and Enter
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (filteredOrganizations.length === 0) {
        return
      }

      const gridColumns = 2
      const maxIndex = filteredOrganizations.length - 1

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault()
          setSelectedIndex((prev) => {
            const newIndex = Math.min(prev + gridColumns, maxIndex)
            // Scroll into view
            setTimeout(
              () =>
                selectedButtonRef.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "nearest",
                }),
              0,
            )
            return newIndex
          })
          break
        case "ArrowUp":
          event.preventDefault()
          setSelectedIndex((prev) => {
            const newIndex = Math.max(prev - gridColumns, 0)
            setTimeout(
              () =>
                selectedButtonRef.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "nearest",
                }),
              0,
            )
            return newIndex
          })
          break
        case "ArrowRight":
          event.preventDefault()
          setSelectedIndex((prev) => {
            const newIndex = prev < maxIndex ? prev + 1 : prev
            setTimeout(
              () =>
                selectedButtonRef.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "nearest",
                }),
              0,
            )
            return newIndex
          })
          break
        case "ArrowLeft":
          event.preventDefault()
          setSelectedIndex((prev) => {
            const newIndex = prev > 0 ? prev - 1 : prev
            setTimeout(
              () =>
                selectedButtonRef.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "nearest",
                }),
              0,
            )
            return newIndex
          })
          break
        case "Enter":
          event.preventDefault()
          if (filteredOrganizations[selectedIndex]) {
            void handleOrganizationSwitch(filteredOrganizations[selectedIndex])
          }
          break
      }
    },
    [filteredOrganizations, selectedIndex, handleOrganizationSwitch],
  )

  // Loading state
  if (!isLoaded) {
    return (
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleClose()
          }
        }}
      >
        <DialogContent className="w-full max-w-2xl overflow-hidden">
          <DialogTitle className="sr-only">{DIALOG_TITLE}</DialogTitle>
          <DialogBody className="py-6">
            <div className="grid grid-cols-2 gap-3 p-1">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="flex min-h-20 items-center gap-3 rounded-xl border border-coval-border p-4"
                >
                  <Skeleton className="size-11 shrink-0 rounded-lg" />
                  <div className="flex flex-1 flex-col gap-2">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              ))}
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
    )
  }

  const loadedOrgs = userMemberships?.data?.length || 0
  const hasMore = userMemberships?.hasNextPage || false

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose()
        }
      }}
    >
      <DialogContent
        className="max-h-[min(80vh,640px)] w-full max-w-2xl overflow-hidden"
        onKeyDown={handleKeyDown}
      >
        <DialogHeader className="shrink-0 items-center gap-1 px-6 pt-6 text-center">
          <DialogTitle className="text-base">{DIALOG_TITLE}</DialogTitle>
          <DialogDescription>
            {filteredOrganizations.length} of {loadedOrgs} organizations
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex min-h-0 flex-col gap-4 px-6 pb-6 pt-4">
          <SearchInput
            placeholder="Search organizations..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            onClear={() => handleSearch("")}
            className="w-full"
            autoFocus
            aria-label="Search organizations"
          />
          {filteredOrganizations.length === 0 ? (
            <div className="flex min-h-56 flex-col items-center justify-center text-center">
              <Building2 className="w-12 h-12 text-coval-text-muted mb-4" />
              <p className="text-coval-text-muted">
                {searchTerm
                  ? "No organizations found"
                  : "No organizations available"}
              </p>
            </div>
          ) : (
            <div className="min-h-0 overflow-y-auto">
              <div className="grid grid-cols-2 gap-3 p-1">
                {filteredOrganizations.map((membership, index) => (
                  <OrganizationButton
                    key={membership.id}
                    membership={membership}
                    isCurrentOrg={currentOrg?.id === membership.organization.id}
                    isHighlighted={index === selectedIndex}
                    isLoading={isLoading}
                    onPress={(selectedMembership) => {
                      void handleOrganizationSwitch(selectedMembership)
                    }}
                    buttonRef={
                      index === selectedIndex
                        ? (selectedButtonRef as React.RefObject<HTMLButtonElement>)
                        : undefined
                    }
                  />
                ))}
              </div>

              {hasMore && (
                <div className="text-center py-4">
                  <Button
                    variant="ghost"
                    onClick={userMemberships.fetchNext}
                    disabled={!userMemberships.hasNextPage}
                  >
                    Load more organizations
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
